import { TargetingFields } from '@openx/types/targeting/targeting';
import type { RequiredTargetingFormType } from '@openx/types/targeting/targetingPresets';
import isEqual from 'lodash/isEqual';

/**
 * `distribution_channel.val` and the `device_type.*_devices` values are comma-separated INTERSECTS sets, so their
 * order carries no meaning to the API. The UI, however, rebuilds them from the option lists in a fixed declaration
 * order, which means a package stored as e.g. "APP,WEB" comes back out of the form as "WEB,APP". Sorting gives one
 * canonical serialisation so that equal sets compare equal.
 *
 * Used for comparison only - see `withNormalizedRenderingContext`.
 */
export const sortSetValues = (value: string | null | undefined): string | null => {
  if (!value) {
    return null;
  }

  const values = value.split(',').filter(Boolean);

  return values.length ? values.sort().join(',') : null;
};

export const normalizeRenderingContext = (
  renderingContext: RequiredTargetingFormType | null | undefined
): RequiredTargetingFormType | null => {
  if (!renderingContext) {
    return null;
  }

  const { device_type, distribution_channel } = renderingContext;

  return {
    ...renderingContext,
    ...(distribution_channel && {
      distribution_channel: { ...distribution_channel, val: sortSetValues(distribution_channel.val) },
    }),
    ...(device_type && {
      device_type: {
        ...device_type,
        desktop_devices: sortSetValues(device_type.desktop_devices),
        mobile_devices: sortSetValues(device_type.mobile_devices),
        tv_devices: sortSetValues(device_type.tv_devices),
      },
    }),
  };
};

type WithRenderingContext = {
  [TargetingFields.INVENTORY_FORMAT]?: RequiredTargetingFormType | null;
};

/**
 * Returns a copy with only `rendering_context` canonicalised, for COMPARISON purposes. It is deliberately not applied
 * to what we send to the API: the order the UI happens to produce is valid on the wire, and rewriting every payload
 * would be a far wider change than this defect warrants.
 */
export const withNormalizedRenderingContext = <T extends WithRenderingContext | null | undefined>(targeting: T): T => {
  if (!targeting?.[TargetingFields.INVENTORY_FORMAT]) {
    return targeting;
  }

  return {
    ...targeting,
    [TargetingFields.INVENTORY_FORMAT]: normalizeRenderingContext(targeting[TargetingFields.INVENTORY_FORMAT]),
  } as T;
};

/**
 * Equality for two targeting states that ignores the ordering inside the comma-separated INTERSECTS sets of
 * `rendering_context`. Without it, a package stored as "APP,WEB" and the same package rebuilt by the UI as
 * "WEB,APP" compare as different, and an untouched package gets saved.
 */
export const isTargetingEqualIgnoringSetOrder = (
  a: WithRenderingContext | null | undefined,
  b: WithRenderingContext | null | undefined
): boolean => isEqual(withNormalizedRenderingContext(a), withNormalizedRenderingContext(b));
